// An organisation's tax categories (F-PAY-11; spec/06 §10.1) as TanStack Query hooks over the generated client,
// `packages/contracts/src/routes/tax-categories.ts`:
//
//   GET   …/organizations/{o}/tax-categories              the categories (India defaults seeded on first read)  settings:read
//   POST  …/organizations/{o}/tax-categories              define one                                            tax:manage
//   PATCH …/organizations/{o}/tax-categories/{id}         edit or archive one                                   tax:manage
//
// Each write refreshes the list. A rate change re-rates the products in the category on the server, so the menu is
// read again too.

import type { CreateTaxCategoryRequest, TaxCategory, UpdateTaxCategoryRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';

export const taxCategoriesKey = (organizationId: string) => ['organizations', organizationId, 'tax-categories'] as const;

export function useTaxCategories(organizationId: string | undefined) {
  const api = useApi();
  return useQuery<readonly TaxCategory[]>({
    queryKey: taxCategoriesKey(organizationId ?? ''),
    queryFn: async () => (await api.listTaxCategories({ path: { organizationId: organizationId ?? '' } })).categories,
    enabled: organizationId !== undefined && organizationId !== '',
    refetchOnWindowFocus: false,
  });
}

function useRefresh(organizationId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: taxCategoriesKey(organizationId) });
    // A new rate re-rates the category's products: the Sell menu reads them again.
    void queryClient.invalidateQueries({ queryKey: ['sell', organizationId] });
  };
}

/** Define a tax category. Audited. */
export function useCreateTaxCategory(organizationId: string) {
  const api = useApi();
  const refresh = useRefresh(organizationId);
  return useMutation({
    mutationFn: (body: CreateTaxCategoryRequest) => api.createTaxCategory({ path: { organizationId }, body }),
    onSuccess: refresh,
  });
}

/** Edit or archive a tax category. Audited. */
export function useUpdateTaxCategory(organizationId: string) {
  const api = useApi();
  const refresh = useRefresh(organizationId);
  return useMutation({
    mutationFn: ({ taxCategoryId, body }: { taxCategoryId: string; body: UpdateTaxCategoryRequest }) =>
      api.updateTaxCategory({ path: { organizationId, taxCategoryId }, body }),
    onSuccess: refresh,
  });
}

/** A rate in basis points as a percentage the accountant types (`1800` → `18`, `250` → `2.5`). */
export function percentOf(bps: number): string {
  return String(bps / 100);
}

/** A typed percentage as basis points, or null when it is not one (0–100, at most two decimals). */
export function bpsOf(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(trimmed)) return null;
  const bps = Math.round(Number(trimmed) * 100);
  return bps >= 0 && bps <= 10_000 ? bps : null;
}
